import { useState, useEffect, useCallback } from 'react';

interface Favorite {
  id: number;
  label: string;
  prompt: string;
  category: string;
  account_id: string | null;
  goal: string | null;
  campaign_type: string | null;
  created_at: string;
}

interface FavoritesProps {
  onSendPrompt: (prompt: string) => void;
  onRestoreContext?: (fav: Favorite) => void;
  currentAccountId?: string;
  currentGoal?: string;
  currentCampaignType?: string;
  visible: boolean;
  onToggle: () => void;
}

export default function Favorites({
  onSendPrompt,
  onRestoreContext,
  currentAccountId,
  currentGoal,
  currentCampaignType,
  visible,
  onToggle,
}: FavoritesProps) {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [favLabel, setFavLabel] = useState('');
  const [favPrompt, setFavPrompt] = useState('');

  const loadFavorites = useCallback(async () => {
    try {
      const res = await fetch('/api/chat/favorites');
      if (res.ok) {
        const data = await res.json();
        setFavorites(data.favorites || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const handleAddFavorite = async () => {
    if (!favLabel.trim() || !favPrompt.trim()) return;
    try {
      const res = await fetch('/api/chat/favorites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: favLabel,
          prompt: favPrompt,
          account_id: currentAccountId || null,
          goal: currentGoal || null,
          campaign_type: currentCampaignType || null,
        }),
      });
      if (res.ok) {
        setFavLabel('');
        setFavPrompt('');
        setShowAddForm(false);
        loadFavorites();
      }
    } catch {}
  };

  const handleDeleteFavorite = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await fetch(`/api/chat/favorites/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setFavorites((prev) => prev.filter((f) => f.id !== id));
      }
    } catch {}
  };

  const handleUseFavorite = (fav: Favorite) => {
    if (onRestoreContext) {
      onRestoreContext(fav);
    }
    onSendPrompt(fav.prompt);
    onToggle();
  };

  return (
    <>
      <button
        className={`command-bar-btn${visible ? ' active' : ''}`}
        onClick={onToggle}
        title="Saved prompts"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill={visible ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <span>Saved</span>
        {favorites.length > 0 && <span className="command-bar-badge">{favorites.length}</span>}
      </button>

      {visible && (
        <div className="command-bar-favorites">
          <div className="command-bar-fav-header">
            <span className="command-bar-fav-title">Saved Prompts</span>
            <button
              className="command-bar-fav-add-btn"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? 'Cancel' : '+ Add'}
            </button>
          </div>
          {showAddForm && (
            <div className="command-bar-fav-form">
              <input
                type="text"
                placeholder="Label (e.g. Weekly Report)"
                value={favLabel}
                onChange={(e) => setFavLabel(e.target.value)}
                className="command-bar-fav-input"
                maxLength={255}
              />
              <textarea
                placeholder="Prompt text..."
                value={favPrompt}
                onChange={(e) => setFavPrompt(e.target.value)}
                className="command-bar-fav-textarea"
                rows={2}
              />
              <div className="command-bar-fav-context-hint">
                {(currentGoal || currentCampaignType || currentAccountId) && (
                  <span>Will save with current filters</span>
                )}
              </div>
              <button
                className="command-bar-fav-save"
                onClick={handleAddFavorite}
                disabled={!favLabel.trim() || !favPrompt.trim()}
              >
                Save
              </button>
            </div>
          )}
          {favorites.length === 0 && !showAddForm ? (
            <div className="command-bar-fav-empty">No saved prompts yet. Click "+ Add" to create one.</div>
          ) : (
            <div className="command-bar-fav-list">
              {favorites.map((f) => (
                <div
                  key={f.id}
                  className="command-bar-fav-item"
                  onClick={() => handleUseFavorite(f)}
                >
                  <div className="command-bar-fav-item-content">
                    <span className="command-bar-fav-item-label">{f.label}</span>
                    <span className="command-bar-fav-item-prompt">{f.prompt}</span>
                    {(f.goal || f.campaign_type) && (
                      <span className="command-bar-fav-item-tags">
                        {f.goal && <span className="command-bar-fav-tag">{f.goal}</span>}
                        {f.campaign_type && <span className="command-bar-fav-tag">{f.campaign_type}</span>}
                      </span>
                    )}
                  </div>
                  <button
                    className="command-bar-fav-item-delete"
                    onClick={(e) => handleDeleteFavorite(f.id, e)}
                    title="Remove"
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
